/**
 * Opus Panel - Main slide-over panel for the AI assistant
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Bot, Send, Loader2, MessageSquarePlus, History, Sparkles } from 'lucide-react';
import { useOpusAIStore, type OpusMessage as MessageType, type OpusToolCall } from '@/stores/opus-ai-store';
import { OpusMessage } from './OpusMessage';
import { OpusToolActivity } from './OpusToolActivity';
import { QuickActions } from './QuickActions';
import { sendOpusMessageStream, fetchOpusConversations, fetchOpusConversation } from '@/lib/api';

export function OpusPanel() {
  const {
    isOpen,
    setIsOpen,
    messages,
    setMessages,
    addMessage,
    updateLastMessage,
    isLoading,
    setIsLoading,
    activeTools,
    addActiveTool,
    removeActiveTool,
    clearActiveTools,
    streamingMessage,
    setStreamingMessage,
    error,
    setError,
    activeConversationId,
    setActiveConversationId,
    startNewConversation,
    conversations,
    setConversations,
    lastUsage,
    setLastUsage,
  } = useOpusAIStore();

  const [inputValue, setInputValue] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load conversations on mount
  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [isOpen]);

  const loadConversations = async () => {
    try {
      const convos = await fetchOpusConversations();
      // Convert string dates to Date objects
      setConversations(
        convos.map((c) => ({
          ...c,
          createdAt: new Date(c.createdAt),
          updatedAt: new Date(c.updatedAt),
        }))
      );
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  };

  const loadConversation = async (conversationId: string) => {
    try {
      const data = await fetchOpusConversation(conversationId);
      setActiveConversationId(conversationId);
      setMessages(
        data.messages.map((m: { id: string; role: 'user' | 'assistant'; content: string; toolCalls?: OpusToolCall[]; createdAt: string }) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          toolCalls: m.toolCalls,
          createdAt: new Date(m.createdAt),
        }))
      );
      setShowHistory(false);
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError('Failed to load conversation');
    }
  };

  const handleSend = useCallback(async (prompt?: string) => {
    const messageText = prompt || inputValue.trim();
    if (!messageText || isLoading) return;

    setInputValue('');
    setError(null);

    // Add user message
    const userMessage: MessageType = {
      id: crypto.randomUUID(),
      role: 'user',
      content: messageText,
      createdAt: new Date(),
    };
    addMessage(userMessage);

    // Add placeholder assistant message
    const assistantMessage: MessageType = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      createdAt: new Date(),
      isStreaming: true,
    };
    addMessage(assistantMessage);

    setIsLoading(true);
    setStreamingMessage('');
    clearActiveTools();

    try {
      // Use streaming endpoint
      let fullContent = '';
      const toolCalls: OpusToolCall[] = [];

      for await (const event of sendOpusMessageStream(messageText, activeConversationId || undefined)) {
        if (event.type === 'text') {
          fullContent += event.content || '';
          setStreamingMessage(fullContent);
        } else if (event.type === 'tool_start') {
          if (event.toolName) {
            addActiveTool(event.toolName);
          }
        } else if (event.type === 'tool_end') {
          if (event.toolName) {
            removeActiveTool(event.toolName);
            toolCalls.push({
              name: event.toolName,
              success: (event.toolResult as { success: boolean })?.success ?? true,
              duration: 0, // Duration not available in stream
            });
          }
        } else if (event.type === 'done') {
          if (event.conversationId) {
            setActiveConversationId(event.conversationId);
          }
          if (event.usage) {
            setLastUsage(event.usage);
          }
        } else if (event.type === 'error') {
          throw new Error(event.error || 'Unknown error');
        }
      }

      // Update the placeholder with final content
      updateLastMessage(fullContent, toolCalls.length > 0 ? toolCalls : undefined);
      setStreamingMessage('');

      // Refresh conversations list
      loadConversations();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      // Remove the placeholder assistant message on error
      setMessages(messages.filter((m) => m.id !== assistantMessage.id));
    } finally {
      setIsLoading(false);
      clearActiveTools();
    }
  }, [inputValue, isLoading, activeConversationId, messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = () => {
    startNewConversation();
    setShowHistory(false);
    inputRef.current?.focus();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={() => setIsOpen(false)}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-[420px] bg-bg-secondary border-l border-white/10 z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-text-primary flex items-center gap-1.5">
                Opus AI
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              </h2>
              <p className="text-xs text-text-tertiary">Your recruitment assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleNewConversation}
              className="p-2 text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors"
              title="New conversation"
            >
              <MessageSquarePlus className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`p-2 rounded-lg transition-colors ${
                showHistory
                  ? 'text-emerald-400 bg-emerald-500/10'
                  : 'text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary'
              }`}
              title="Conversation history"
            >
              <History className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* History Panel */}
        {showHistory && (
          <div className="border-b border-white/10 bg-bg-tertiary/50">
            <div className="px-4 py-2 text-xs font-medium text-text-tertiary uppercase tracking-wide">
              Recent Conversations
            </div>
            <div className="max-h-48 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="px-4 py-3 text-sm text-text-tertiary">
                  No previous conversations
                </div>
              ) : (
                conversations.map((convo) => (
                  <button
                    key={convo.id}
                    onClick={() => loadConversation(convo.id)}
                    className={`
                      w-full px-4 py-2 text-left hover:bg-bg-elevated transition-colors
                      ${activeConversationId === convo.id ? 'bg-bg-elevated' : ''}
                    `}
                  >
                    <div className="text-sm text-text-primary truncate">
                      {convo.title}
                    </div>
                    <div className="text-xs text-text-tertiary">
                      {convo.messageCount} messages
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <Bot className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-medium text-text-primary mb-2">
                How can I help you?
              </h3>
              <p className="text-sm text-text-secondary mb-6 max-w-xs mx-auto">
                I can search candidates, manage jobs, update pipelines, and provide insights.
              </p>
              <QuickActions onAction={handleSend} disabled={isLoading} />
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <OpusMessage
                  key={message.id || index}
                  message={
                    message.isStreaming
                      ? { ...message, content: streamingMessage || message.content }
                      : message
                  }
                />
              ))}
            </>
          )}

          {/* Active tool activity */}
          {activeTools.length > 0 && (
            <div className="px-4">
              <OpusToolActivity activeTools={activeTools} />
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="px-4 py-3 bg-error-500/10 border border-error-500/20 rounded-lg text-sm text-error-500">
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-white/10 p-4">
          {/* Usage info */}
          {lastUsage && (
            <div className="mb-2 text-xs text-text-tertiary text-right">
              {lastUsage.inputTokens + lastUsage.outputTokens} tokens (${lastUsage.costUsd.toFixed(4)})
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Opus anything..."
                disabled={isLoading}
                rows={1}
                className="
                  w-full px-4 py-3 pr-12
                  bg-bg-tertiary border border-white/10 rounded-xl
                  text-sm text-text-primary placeholder:text-text-tertiary
                  focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50
                  resize-none
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={() => handleSend()}
              disabled={isLoading || !inputValue.trim()}
              className="
                p-3 rounded-xl
                bg-emerald-500 hover:bg-emerald-600
                disabled:bg-emerald-500/50 disabled:cursor-not-allowed
                text-white
                transition-colors
              "
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Quick actions when conversation started */}
          {messages.length > 0 && !isLoading && (
            <div className="mt-3">
              <QuickActions onAction={handleSend} disabled={isLoading} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
